import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft, Users, BarChart3, Lightbulb, Search, Plus, Trash2,
  UserCog, Loader2, Eye, Ban, CheckCircle, Clock, ThumbsUp,
  TrendingUp, FileText, Calendar, Shield, ExternalLink, RefreshCw
} from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';
import Footer from '@/components/Footer';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell
} from 'recharts';
import SafeChart from '@/components/SafeChart';

const TIER_COLORS = {
  trial: 'bg-slate-100 text-slate-700',
  professional: 'bg-blue-100 text-blue-700',
  business: 'bg-purple-100 text-purple-700',
};

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  under_review: 'bg-yellow-100 text-yellow-700',
  planned: 'bg-purple-100 text-purple-700',
  in_progress: 'bg-orange-100 text-orange-700',
  shipped: 'bg-green-100 text-green-700',
  not_planned: 'bg-slate-100 text-slate-700',
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  
  // Dashboard State
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Users State
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userTierFilter, setUserTierFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editUserData, setEditUserData] = useState({});
  const [showDeleteUser, setShowDeleteUser] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserData, setNewUserData] = useState({ email: '', name: '', tier: 'professional' });
  
  // Ideas State
  const [ideas, setIdeas] = useState([]);
  const [ideasStats, setIdeasStats] = useState(null);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [ideaUpdate, setIdeaUpdate] = useState({});
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activeTab === 'dashboard') loadStats();
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'ideas') { loadIdeas(); loadIdeasStats(); }
  }, [activeTab]);

  // Reload users when filter changes
  useEffect(() => {
    if (activeTab === 'users') loadUsers();
  }, [userTierFilter]);

  // Load Dashboard Stats
  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Load Users
  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const params = {};
      if (userSearch) params.search = userSearch;
      if (userTierFilter && userTierFilter !== 'all') params.tier = userTierFilter;
      
      const response = await adminAPI.getUsers(params);
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadUserDetail = async (userId) => {
    setUserDetailLoading(true);
    setSelectedUser(userId);
    try {
      const response = await adminAPI.getUser(userId);
      setUserDetail(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
    } finally {
      setUserDetailLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await adminAPI.updateUser(selectedUser, editUserData);
      loadUsers();
      if (userDetail) loadUserDetail(selectedUser);
      setShowEditUser(false);
    } catch (error) {
      console.error('Failed to update user:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!showDeleteUser) return;
    setSaving(true);
    try {
      await adminAPI.deleteUser(showDeleteUser);
      loadUsers();
      setShowDeleteUser(null);
      setSelectedUser(null);
      setUserDetail(null);
    } catch (error) {
      console.error('Failed to delete user:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.name) return;
    setSaving(true);
    try {
      const response = await adminAPI.createUser(newUserData);
      loadUsers();
      setShowCreateUser(false);
      setNewUserData({ email: '', name: '', tier: 'professional' });
      // Show temporary password if generated
      if (response.data.temporary_password) {
        alert(`User created! Temporary password: ${response.data.temporary_password}`);
      }
    } catch (error) {
      console.error('Failed to create user:', error);
      alert(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const response = await adminAPI.impersonateUser(userId);
      // Store original token to return later
      localStorage.setItem('admin_token', localStorage.getItem('token'));
      localStorage.setItem('token', response.data.access_token);
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Failed to impersonate:', error);
    }
  };

  // Load Ideas
  const loadIdeas = async () => {
    setIdeasLoading(true);
    try {
      const response = await adminAPI.getIdeas();
      setIdeas(response.data.ideas);
    } catch (error) {
      console.error('Failed to load ideas:', error);
    } finally {
      setIdeasLoading(false);
    }
  };

  const loadIdeasStats = async () => {
    try {
      const response = await adminAPI.getIdeasStats();
      setIdeasStats(response.data);
    } catch (error) {
      console.error('Failed to load ideas stats:', error);
    }
  };

  const handleUpdateIdea = async () => {
    if (!selectedIdea) return;
    setSaving(true);
    try {
      await adminAPI.updateIdea(selectedIdea.id, ideaUpdate);
      loadIdeas();
      loadIdeasStats();
      setSelectedIdea(null);
      setIdeaUpdate({});
    } catch (error) {
      console.error('Failed to update idea:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-page">
      {/* Header */}
      <header className="border-b bg-slate-900 text-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-white hover:bg-slate-800" data-testid="admin-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-yellow-400" />
              <h1 className="text-lg font-bold">Admin Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400">Logged in as {user?.email}</span>
            <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800" onClick={logout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSearchParams({ tab: v }); }} className="space-y-6">
          <TabsList className="bg-white">
            <TabsTrigger value="dashboard" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="ideas" className="gap-2">
              <Lightbulb className="h-4 w-4" />
              Ideas
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {statsLoading ? (
              <div className="grid grid-cols-4 gap-4">
                {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
              </div>
            ) : stats && (
              <>
                {/* Stats Cards */}
                <div className="grid md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-100 rounded-lg">
                          <Users className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.total_users}</p>
                          <p className="text-sm text-slate-500">Total Users</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-green-100 rounded-lg">
                          <TrendingUp className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.active_users}</p>
                          <p className="text-sm text-slate-500">Active (7 days)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-100 rounded-lg">
                          <FileText className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.total_projects}</p>
                          <p className="text-sm text-slate-500">Total Projects</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-orange-100 rounded-lg">
                          <BarChart3 className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.total_reports}</p>
                          <p className="text-sm text-slate-500">Total Reports</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Tier Breakdown */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Users by Tier</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">Trial</span>
                          <span className="font-bold">{stats.users_by_tier.trial}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">Professional</span>
                          <span className="font-bold text-blue-600">{stats.users_by_tier.professional}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">Business</span>
                          <span className="font-bold text-purple-600">{stats.users_by_tier.business}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Signups</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">This Week</span>
                          <span className="font-bold text-green-600">+{stats.new_signups_week}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">This Month</span>
                          <span className="font-bold text-blue-600">+{stats.new_signups_month}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Analytics Charts */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Signups Over Time Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Signups Over Time (Last 30 Days)</CardTitle>
                      <CardDescription>Daily new user registrations</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <SafeChart minHeight={250} height={250}>
                        <AreaChart
                          data={(stats.signups_over_time || []).map(d => ({
                            date: d._id?.substring(5) || '', // MM-DD format
                            signups: d.count || 0
                          }))}
                          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient id="signupGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                            labelStyle={{ fontWeight: 600 }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="signups" 
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            fill="url(#signupGradient)" 
                            name="Signups"
                          />
                        </AreaChart>
                      </SafeChart>
                    </CardContent>
                  </Card>

                  {/* User Tier Distribution Pie Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>User Distribution by Tier</CardTitle>
                      <CardDescription>Breakdown of subscription tiers</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <SafeChart minHeight={250} height={250}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Trial', value: stats.users_by_tier.trial || 0, color: '#94a3b8' },
                              { name: 'Professional', value: stats.users_by_tier.professional || 0, color: '#3b82f6' },
                              { name: 'Business', value: stats.users_by_tier.business || 0, color: '#8b5cf6' }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={3}
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            labelLine={false}
                          >
                            {[
                              { name: 'Trial', value: stats.users_by_tier.trial || 0, color: '#94a3b8' },
                              { name: 'Professional', value: stats.users_by_tier.professional || 0, color: '#3b82f6' },
                              { name: 'Business', value: stats.users_by_tier.business || 0, color: '#8b5cf6' }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                          />
                        </PieChart>
                      </SafeChart>
                    </CardContent>
                  </Card>
                </div>

                {/* Activity Metrics */}
                <div className="grid md:grid-cols-3 gap-4">
                  <Card className="border-l-4 border-l-green-500">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Weekly Active Users (WAU)</p>
                          <p className="text-3xl font-bold text-green-600">{stats.active_users}</p>
                        </div>
                        <TrendingUp className="h-8 w-8 text-green-200" />
                      </div>
                      <p className="text-xs text-slate-400 mt-2">Users active in last 7 days</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Monthly Active Users (MAU)</p>
                          <p className="text-3xl font-bold text-blue-600">{stats.active_users_month || stats.active_users}</p>
                        </div>
                        <Users className="h-8 w-8 text-blue-200" />
                      </div>
                      <p className="text-xs text-slate-400 mt-2">Users active in last 30 days</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-purple-500">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Avg Reports/Project</p>
                          <p className="text-3xl font-bold text-purple-600">
                            {stats.total_projects > 0 ? (stats.total_reports / stats.total_projects).toFixed(1) : '0'}
                          </p>
                        </div>
                        <FileText className="h-8 w-8 text-purple-200" />
                      </div>
                      <p className="text-xs text-slate-400 mt-2">Average reports per project</p>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadUsers()}
                    className="pl-10 w-64"
                  />
                </div>
                <Select value={userTierFilter} onValueChange={(v) => { setUserTierFilter(v); }}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="All Tiers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={loadUsers}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <Button onClick={() => setShowCreateUser(true)} className="gap-2" data-testid="create-user-btn">
                <Plus className="h-4 w-4" />
                Create User
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Users List */}
              <Card className="md:col-span-1" data-testid="users-list-card">
                <CardHeader>
                  <CardTitle>Users ({usersTotal})</CardTitle>
                </CardHeader>
                <CardContent className="max-h-[600px] overflow-y-auto">
                  {usersLoading ? (
                    <div className="space-y-2">
                      {[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}
                    </div>
                  ) : users.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No users found</p>
                  ) : (
                    <div className="space-y-2">
                      {users.map(u => (
                        <div
                          key={u.id}
                          className={`p-3 rounded-lg cursor-pointer transition-colors ${selectedUser === u.id ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'}`}
                          onClick={() => loadUserDetail(u.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <p className="text-sm text-slate-500">{u.email}</p>
                            </div>
                            <div className="text-right">
                              <Badge className={`${TIER_COLORS[u.tier]} border-0`}>{u.tier}</Badge>
                              {u.suspended && <Badge variant="destructive" className="ml-1">Suspended</Badge>}
                              {u.is_admin && <Badge className="ml-1 bg-yellow-100 text-yellow-700 border-0">Admin</Badge>}
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">{u.project_count} projects</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* User Detail */}
              <Card className="md:col-span-1">
                <CardHeader>
                  <CardTitle>User Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedUser ? (
                    <p className="text-slate-500 text-center py-8">Select a user to view details</p>
                  ) : userDetailLoading ? (
                    <Skeleton className="h-64" />
                  ) : userDetail ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-bold">{userDetail.user.name}</h3>
                          <p className="text-slate-500">{userDetail.user.email}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => {
                            setEditUserData({
                              name: userDetail.user.name,
                              tier: userDetail.user.tier,
                              suspended: userDetail.user.suspended,
                            });
                            setShowEditUser(true);
                          }}>
                            <UserCog className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleImpersonate(userDetail.user.id)}>
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Login As
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Tier</p>
                          <Badge className={`${TIER_COLORS[userDetail.user.tier]} border-0`}>{userDetail.user.tier}</Badge>
                        </div>
                        <div>
                          <p className="text-slate-500">Status</p>
                          <Badge className={userDetail.user.suspended ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                            {userDetail.user.suspended ? 'Suspended' : 'Active'}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-slate-500">Projects</p>
                          <p className="font-medium">{userDetail.projects.length}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Joined</p>
                          <p className="font-medium">{formatDate(userDetail.user.created_at)}</p>
                        </div>
                      </div>

                      {userDetail.projects.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-slate-500 mb-2">Projects</p>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {userDetail.projects.map(p => (
                              <div key={p.id} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                                <span>{p.name}</span>
                                <span className="text-slate-500">{p.report_count} reports</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 pt-4 border-t">
                        <Button variant="destructive" size="sm" onClick={() => setShowDeleteUser(userDetail.user.id)}>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete User
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Ideas Tab */}
          <TabsContent value="ideas" className="space-y-6">
            {/* Ideas Stats */}
            {ideasStats && (
              <div className="grid md:grid-cols-6 gap-4">
                <Card className="col-span-1">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold">{ideasStats.total}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </CardContent>
                </Card>
                {Object.entries(ideasStats.by_status).map(([status, count]) => (
                  <Card key={status} className="col-span-1">
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-xs text-slate-500 capitalize">{status.replace('_', ' ')}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Ideas List */}
              <Card>
                <CardHeader>
                  <CardTitle>Feature Requests</CardTitle>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto">
                  {ideasLoading ? (
                    <Skeleton className="h-64" />
                  ) : ideas.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No ideas submitted yet</p>
                  ) : (
                    <div className="space-y-2">
                      {ideas.map(idea => (
                        <div
                          key={idea.id}
                          className={`p-3 rounded-lg cursor-pointer transition-colors ${selectedIdea?.id === idea.id ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'}`}
                          onClick={() => { setSelectedIdea(idea); setIdeaUpdate({ status: idea.status, admin_notes: idea.admin_notes || '', admin_response: idea.admin_response || '' }); }}
                        >
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{idea.title}</p>
                            <div className="flex items-center gap-2">
                              <span className="flex items-center gap-1 text-sm text-slate-500">
                                <ThumbsUp className="h-3 w-3" />
                                {idea.votes}
                              </span>
                              <Badge className={`${STATUS_COLORS[idea.status]} border-0 text-xs`}>
                                {idea.status.replace('_', ' ')}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{idea.user_name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Idea Detail */}
              <Card>
                <CardHeader>
                  <CardTitle>Idea Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedIdea ? (
                    <p className="text-slate-500 text-center py-8">Select an idea to manage</p>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-bold">{selectedIdea.title}</h3>
                        <p className="text-sm text-slate-500">
                          by {selectedIdea.user_name} • {formatDate(selectedIdea.created_at)}
                        </p>
                      </div>
                      <p className="text-slate-600">{selectedIdea.description}</p>
                      
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select
                          value={ideaUpdate.status}
                          onValueChange={(v) => setIdeaUpdate({ ...ideaUpdate, status: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="under_review">Under Review</SelectItem>
                            <SelectItem value="planned">Planned</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="not_planned">Not Planned</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Admin Notes (Internal)</Label>
                        <Textarea
                          value={ideaUpdate.admin_notes || ''}
                          onChange={(e) => setIdeaUpdate({ ...ideaUpdate, admin_notes: e.target.value })}
                          placeholder="Internal notes..."
                          rows={2}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Public Response</Label>
                        <Textarea
                          value={ideaUpdate.admin_response || ''}
                          onChange={(e) => setIdeaUpdate({ ...ideaUpdate, admin_response: e.target.value })}
                          placeholder="Response visible to users..."
                          rows={2}
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button onClick={handleUpdateIdea} disabled={saving}>
                          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Save Changes
                        </Button>
                        <Button variant="outline" onClick={() => setSelectedIdea(null)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editUserData.name || ''}
                onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Tier</Label>
              <Select
                value={editUserData.tier}
                onValueChange={(v) => setEditUserData({ ...editUserData, tier: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="suspended"
                checked={editUserData.suspended || false}
                onChange={(e) => setEditUserData({ ...editUserData, suspended: e.target.checked })}
              />
              <Label htmlFor="suspended">Suspended</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUser(false)}>Cancel</Button>
            <Button onClick={handleUpdateUser} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>Create a new user account. A temporary password will be generated.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newUserData.name}
                onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Tier</Label>
              <Select
                value={newUserData.tier}
                onValueChange={(v) => setNewUserData({ ...newUserData, tier: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={saving || !newUserData.email || !newUserData.name}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!showDeleteUser} onOpenChange={() => setShowDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the user and all their projects and reports. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
};

export default AdminPage;
